import React, { useState } from 'react'
import {
  Form,
  Input,
  Button,
  Space,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Table,
  Modal,
  InputNumber,
  Divider,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import * as Types from '@/types'

interface DailyReportHeaderProps {
  headerData?: Types.HeaderData
  onUpdate: (data: Types.HeaderDataUpdateRequest) => Promise<void>
  loading?: boolean
}

interface PersonnelEntry {
  id?: string
  name: string
  role: string
  hours?: number
}

export const DailyReportHeader: React.FC<DailyReportHeaderProps> = ({
  headerData,
  onUpdate,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [personnelForm] = Form.useForm()
  const [error, setError] = useState<string | null>(null)
  const [personnel, setPersonnel] = useState<PersonnelEntry[]>(headerData?.field_personnel || [])
  const [isPersonnelModalVisible, setIsPersonnelModalVisible] = useState(false)
  const [editingPersonnel, setEditingPersonnel] = useState<PersonnelEntry | null>(null)

  React.useEffect(() => {
    if (headerData?.job_info) {
      form.setFieldsValue(headerData.job_info)
    }
  }, [headerData, form])

  const handleJobInfoSubmit = async (values: any) => {
    try {
      setError(null)
      const payload: Types.HeaderDataUpdateRequest = {
        job_info: {
          client: values.client,
          location: values.location,
          well_site: values.well_site,
          pad_id: values.pad_id,
          service_type: values.service_type,
          wellhead_connection_type: values.wellhead_connection_type,
          wellhead_connection_size: values.wellhead_connection_size,
          expected_pressure: values.expected_pressure,
        },
        field_personnel: personnel,
      }
      await onUpdate(payload)
    } catch (err: any) {
      setError(err.message || 'Failed to update header')
    }
  }

  const handleAddPersonnel = () => {
    setEditingPersonnel(null)
    personnelForm.resetFields()
    setIsPersonnelModalVisible(true)
  }

  const handleEditPersonnel = (record: PersonnelEntry) => {
    setEditingPersonnel(record)
    personnelForm.setFieldsValue(record)
    setIsPersonnelModalVisible(true)
  }

  const handleDeletePersonnel = (id: string | undefined) => {
    const newPersonnel = personnel.filter((p) => p.id !== id && p.name !== id)
    setPersonnel(newPersonnel)
  }

  const handlePersonnelModalOk = async () => {
    try {
      const values = await personnelForm.validateFields()

      if (editingPersonnel) {
        // Update existing
        const newPersonnel = personnel.map((p) =>
          p.id === editingPersonnel.id || p.name === editingPersonnel.name ? { ...p, ...values } : p
        )
        setPersonnel(newPersonnel)
      } else {
        // Add new
        setPersonnel([...personnel, values])
      }

      setIsPersonnelModalVisible(false)
      personnelForm.resetFields()
      setEditingPersonnel(null)
    } catch (err) {
      // Validation failed
    }
  }

  const handlePersonnelModalCancel = () => {
    setIsPersonnelModalVisible(false)
    personnelForm.resetFields()
    setEditingPersonnel(null)
  }

  const personnelColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours: number | undefined) => hours ? `${hours.toFixed(1)} hrs` : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: PersonnelEntry) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditPersonnel(record)}
            title="Edit"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePersonnel(record.id || record.name)}
            title="Delete"
          />
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '20px 0' }}>
      {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} closable />}

      <Spin spinning={loading}>
        {/* Job Information Section */}
        <Card title="Job Information" size="small" style={{ marginBottom: '24px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleJobInfoSubmit}
            disabled={loading}
            requiredMark="optional"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="client" label="Client Name">
                  <Input placeholder="e.g., Tecpetrol" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="location" label="Location / Province">
                  <Input placeholder="e.g., Neuquén, Argentina" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="well_site" label="Well Site / Block">
                  <Input placeholder="e.g., Los Toldos 01-PL-01" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="pad_id" label="Pad / Sector ID">
                  <Input placeholder="e.g., P1662" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="service_type" label="Service Type">
                  <Input placeholder="e.g., Wireline Unconventional" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <h4>Wellhead Connection Details</h4>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="wellhead_connection_type" label="Wellhead Connection Type">
                  <Input placeholder="e.g., Cameron conventional" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="wellhead_connection_size" label="Connection Size">
                  <Input placeholder="e.g., 1.9/16 API EUE" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="expected_pressure" label="Expected Pressure">
                  <Input placeholder="e.g., 350 bar" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Job Information
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Field Personnel Section */}
        <Card
          title="Field Personnel"
          size="small"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPersonnel} size="small">
              Add Personnel
            </Button>
          }
        >
          {personnel.length > 0 ? (
            <Table
              dataSource={personnel.map((p, idx) => ({ ...p, key: p.id || idx }))}
              columns={personnelColumns}
              pagination={false}
              bordered
              size="small"
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              No field personnel added yet. Click "Add Personnel" to begin.
            </div>
          )}
        </Card>

        {/* Personnel Modal */}
        <Modal
          title={editingPersonnel ? 'Edit Personnel' : 'Add Personnel'}
          open={isPersonnelModalVisible}
          onOk={handlePersonnelModalOk}
          onCancel={handlePersonnelModalCancel}
        >
          <Form form={personnelForm} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter personnel name' }]}
            >
              <Input placeholder="e.g., Andrés García" />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please enter personnel role' }]}
            >
              <Input placeholder="e.g., Field Engineer" />
            </Form.Item>

            <Form.Item name="hours" label="Hours Worked (Optional)">
              <InputNumber min={0} max={24} step={0.5} placeholder="0" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </div>
  )
}

export default DailyReportHeader
